import { useState, useEffect, useRef } from "react";
import { FaWalking, FaBus } from "react-icons/fa";
import { MapGoogle } from "../components/map";
import { LinhasOnibus, PartidasLinha } from "../features/lines";
import { PartidasParada } from "../features/stops";
import { RoutePlanner, DetalhesRotaPlanner } from "../features/routes";
import { RouteStepNavigator, FloatingNav } from "../components/navigation";
import { BottomSheet } from "../components/ui";
import { useVehicleTracking } from "../hooks/useVehicleTracking";
import { busApi } from "../services/api/busApi";
import "../styles/App.css";

function PlannerPage() {
  const [activeTab, setActiveTab] = useState(null);
  const [sheetContent, setSheetContent] = useState(null);
  const [sheetHeight, setSheetHeight] = useState("50%");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedRouteShortName, setSelectedRouteShortName] = useState(null);
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [routePath, setRoutePath] = useState(null);
  const [centerMapPosition, setCenterMapPosition] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [focusVehicle, setFocusVehicle] = useState(null);
  const [focusedVehicleId, setFocusedVehicleId] = useState(null);
  const [trackingRouteShortNames, setTrackingRouteShortNames] = useState([]);

  const [userLocation, setUserLocation] = useState(null);
  const [nearbyStops, setNearbyStops] = useState([]);
  const [nearbyStopsLoading, setNearbyStopsLoading] = useState(false);
  const [nearbyStopsError, setNearbyStopsError] = useState(null);

  const [tripOverlay, setTripOverlay] = useState(null);
  const [tripOverlayLoading, setTripOverlayLoading] = useState(false);
  const [tripOverlayError, setTripOverlayError] = useState(null);
  const tripOverlayCacheRef = useRef(new Map());

  const focusVehicleTimeoutRef = useRef(null);
  const clearFocusVehicleTimeoutRef = useRef(null);

  // Polling global: mantém atualização quando o BottomSheet está fechado,
  // para o veículo não “congelar” no mapa após clicar em "Ver no mapa".
  const shouldTrackInBackground =
    sheetContent === null && trackingRouteShortNames.length > 0;

  const { vehicles: trackedVehicles } = useVehicleTracking(
    {},
    30000,
    shouldTrackInBackground
  );

  useEffect(() => {
    if (!shouldTrackInBackground) return;
    if (!trackedVehicles.length) {
      setVehicles([]);
      return;
    }

    const filtered = trackedVehicles.filter((v) =>
      trackingRouteShortNames.includes(v.routeShortName)
    );
    setVehicles(filtered);

    // Se ainda estiver focado em algum veículo, manter posição mais recente
    if (focusedVehicleId) {
      const updatedFocused = trackedVehicles.find(
        (v) => v.vehicleId === focusedVehicleId
      );
      if (updatedFocused) {
        setFocusVehicle(updatedFocused);
      }
    }
  }, [
    shouldTrackInBackground,
    trackedVehicles,
    trackingRouteShortNames,
    focusedVehicleId,
  ]);

  const handleTabChange = (tab) => {
    if (activeTab === tab) {
      setActiveTab(null);
      setSheetContent(null);
      // Limpa rotas quando fechar
      setRoutePath(null);
      setRouteDetails(null);
      return;
    }

    setActiveTab(tab);
    setSelectedRouteId(null);

    if (tab === "linhas") {
      setSheetContent("linhas");
      setSheetHeight("60%");
    } else if (tab === "rotas") {
      setSheetContent("rotas");
      setSheetHeight("70%");
      // Limpa rotas anteriores ao abrir o planejador
      setRoutePath(null);
      setRouteDetails(null);

      // Limpa overlay do veículo ao entrar no planejador
      // para evitar sobreposição entre o trajeto do ônibus e a rota planejada.
      setTripOverlay(null);
      setTripOverlayLoading(false);
      setTripOverlayError(null);
    } else if (tab === "info") {
      setSheetContent("info");
      setSheetHeight("40%");
    }
  };

  const handleLinhaSelect = (linha) => {
    setSelectedRouteId(linha?.routeId ?? null);
    setSelectedRouteShortName(linha?.shortName ?? null);
    setSheetContent("horarios");
    setSheetHeight("70%");
  };

  const handleBackToLinhas = () => {
    setSelectedRouteId(null);
    setSelectedRouteShortName(null);
    setSheetContent("linhas");
    setSheetHeight("60%");
  };

  const handleMarkerClick = (stopId) => {
    setSelectedStopId(stopId);
    setSheetContent("ponto");
    setSheetHeight("40%");
    setActiveTab(null);
  };

  const handleCloseSheet = () => {
    setActiveTab(null);
    setSheetContent(null);
    setSelectedRouteId(null);
    setSelectedRouteShortName(null);
    setSelectedStopId(null);
    // Limpa rotas do mapa quando fechar
    setRoutePath(null);
    setRouteDetails(null);
    setVehicles([]);
    setFocusVehicle(null);
    setFocusedVehicleId(null);
    setTrackingRouteShortNames([]);

    setUserLocation(null);
    setNearbyStops([]);
    setNearbyStopsLoading(false);
    setNearbyStopsError(null);

    setTripOverlay(null);
    setTripOverlayLoading(false);
    setTripOverlayError(null);
  };

  const loadTripOverlayForVehicle = async (vehicle) => {
    const normalizeIdentifier = (value) => {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      const lowered = trimmed.toLowerCase();
      if (lowered === "null" || lowered === "undefined") return null;
      return trimmed;
    };

    const shapeLookupId =
      normalizeIdentifier(vehicle?.vehiclePrefix) ||
      normalizeIdentifier(vehicle?.tripId) ||
      normalizeIdentifier(vehicle?.tripIdRaw) ||
      normalizeIdentifier(vehicle?.routeId) ||
      normalizeIdentifier(vehicle?.routeShortName) ||
      null;

    if (!shapeLookupId) {
      setTripOverlay(null);
      setTripOverlayError(
        new Error(
          "Veículo sem identificador (vehiclePrefix/tripId/route); não é possível desenhar o percurso."
        )
      );
      return;
    }

    const cached = tripOverlayCacheRef.current.get(shapeLookupId);
    if (cached) {
      setTripOverlayError(null);
      setTripOverlay(cached);
      return;
    }

    setTripOverlayError(null);
    setTripOverlayLoading(true);
    try {
      // 1) Sempre buscar o shape sem exigir tripId (robusto para feeds que vêm sem tripId)
      const res = await busApi.getTripShape(shapeLookupId, {
        includeStops: false,
      });

      const data = res?.data;
      const overlayBase = data ? { ...data, tripId: shapeLookupId } : data;
      tripOverlayCacheRef.current.set(shapeLookupId, overlayBase);
      setTripOverlay(overlayBase);

      // 2) Em seguida, buscar paradas (stops) e mesclar, para o mapa conseguir exibir as paradas.
      // Não bloqueia a renderização do shape.
      try {
        const resStops = await busApi.getTripShape(shapeLookupId, {
          includeStops: true,
        });
        const dataStops = resStops?.data;

        if (dataStops && Array.isArray(dataStops?.stops) && dataStops.stops.length > 0) {
          const mergedOverlay = {
            ...overlayBase,
            ...dataStops,
            tripId: shapeLookupId,
          };
          tripOverlayCacheRef.current.set(shapeLookupId, mergedOverlay);
          setTripOverlay(mergedOverlay);
        }
      } catch (errStops) {
        // Stops são um "extra"; se falhar, mantém o shape.
        console.warn("Não foi possível carregar paradas do percurso:", errStops);
      }
    } catch (err) {
      console.error("Erro ao buscar percurso (shape) do veículo:", err);
      setTripOverlay(null);
      setTripOverlayError(err);
    } finally {
      setTripOverlayLoading(false);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("Geolocalização não suportada neste dispositivo."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => {
          reject(err);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
      );
    });
  };

  const handleNearbyStopsClick = async () => {
    setNearbyStopsError(null);
    setNearbyStopsLoading(true);

    try {
      const loc = await getUserLocation();
      setUserLocation(loc);

      // Centraliza o mapa no usuário
      setCenterMapPosition({
        lat: loc.lat,
        lon: loc.lng,
        name: "Minha localização",
      });
      setTimeout(() => setCenterMapPosition(null), 1000);

      const radius = 500;
      const limit = 20;
      const res = await busApi.getNearbyStops({
        lat: loc.lat,
        lng: loc.lng,
        radius,
        limit,
      });
      const stops = res?.data?.stops || [];
      setNearbyStops(stops);
    } catch (err) {
      console.error("Erro ao buscar paradas perto do usuário:", err);
      setNearbyStops([]);
      setNearbyStopsError(err);
    } finally {
      setNearbyStopsLoading(false);
    }
  };

  const handleVehiclesUpdate = (updatedVehicles) => {
    setVehicles(updatedVehicles);
  };

  const handleVehicleClick = (vehicle) => {
    // Limpar timers pendentes (evita múltiplos pans/zooms em cliques rápidos)
    if (focusVehicleTimeoutRef.current) {
      clearTimeout(focusVehicleTimeoutRef.current);
      focusVehicleTimeoutRef.current = null;
    }
    if (clearFocusVehicleTimeoutRef.current) {
      clearTimeout(clearFocusVehicleTimeoutRef.current);
      clearFocusVehicleTimeoutRef.current = null;
    }

    // Resetar o foco antes de refocar (permite refocar no mesmo veículo sem depender do polling)
    setFocusVehicle(null);
    setFocusedVehicleId(vehicle?.vehicleId || null);
    setTrackingRouteShortNames(
      vehicle?.routeShortName ? [vehicle.routeShortName] : []
    );

    // Buscar percurso completo + paradas (quando disponível)
    const tripPromise = loadTripOverlayForVehicle(vehicle);

    // Fechar o BottomSheet para o usuário ver o mapa
    setActiveTab(null);
    setSheetContent(null);

    // Depois que o percurso carregar, aguarda 3s e centraliza no ônibus.
    // Isso evita o comportamento atual de só centralizar quando chega o próximo polling (30s).
    Promise.resolve(tripPromise)
      .catch(() => {
        // Erro já é tratado/logado em loadTripOverlayForVehicle
      })
      .finally(() => {
        focusVehicleTimeoutRef.current = setTimeout(() => {
          setFocusVehicle(vehicle);
        }, 3000);

        // Limpar o foco logo depois para permitir refocar no mesmo veículo em novos cliques
        clearFocusVehicleTimeoutRef.current = setTimeout(() => {
          setFocusVehicle(null);
        }, 4500);
      });
  };

  useEffect(() => {
    return () => {
      if (focusVehicleTimeoutRef.current) {
        clearTimeout(focusVehicleTimeoutRef.current);
        focusVehicleTimeoutRef.current = null;
      }
      if (clearFocusVehicleTimeoutRef.current) {
        clearTimeout(clearFocusVehicleTimeoutRef.current);
        clearFocusVehicleTimeoutRef.current = null;
      }
    };
  }, []);

  // Clique no marker do ônibus no mapa: desenha percurso/paradas sem fechar BottomSheet
  const handleVehicleMarkerClick = (vehicle) => {
    loadTripOverlayForVehicle(vehicle);
  };

  const handleRouteFound = (path, details) => {
    setRoutePath(path);
    setRouteDetails(details);
  };

  const handleViewRouteDetails = () => {
    setSheetContent("navegandoRota"); // Muda para estado especial de navegação
    setCurrentStepIndex(0);

    // Centraliza no primeiro passo automaticamente
    if (routeDetails?.segments?.[0]) {
      const firstSegment = routeDetails.segments[0];
      const lat = firstSegment.fromCoordinates?.[0];
      const lon = firstSegment.fromCoordinates?.[1];
      if (lat && lon) {
        setCenterMapPosition({ lat, lon, name: firstSegment.from });
      }
    }
  };

  const handleBackToRoutePlanner = () => {
    setSheetContent("rotas");
    setSheetHeight("70%");
    setCurrentStepIndex(0);
    // Mantém a rota no mapa
  };

  const handleStepClick = (lat, lon, name) => {
    setCenterMapPosition({ lat, lon, name });
    setTimeout(() => {
      setCenterMapPosition(null);
    }, 1000);
  };

  const handleNextStep = () => {
    if (!routeDetails?.segments) return;
    const newIndex = Math.min(
      currentStepIndex + 1,
      routeDetails.segments.length - 1
    );
    setCurrentStepIndex(newIndex);
    centerOnStep(newIndex);
  };

  const handlePreviousStep = () => {
    const newIndex = Math.max(currentStepIndex - 1, 0);
    setCurrentStepIndex(newIndex);
    centerOnStep(newIndex);
  };

  const centerOnStep = (stepIndex) => {
    if (!routeDetails?.segments?.[stepIndex]) return;
    const segment = routeDetails.segments[stepIndex];
    const lat = segment.fromCoordinates?.[0];
    const lon = segment.fromCoordinates?.[1];
    if (lat && lon) {
      setCenterMapPosition({ lat, lon, name: segment.from });
      setTimeout(() => setCenterMapPosition(null), 1000);
    }
  };

  const handleToggleSheet = () => {
    if (sheetContent === "navegandoRota") {
      // Se está navegando, volta para mostrar detalhes expandidos
      setSheetContent("detalhesRota");
      setSheetHeight("75%");
    } else if (sheetContent === "detalhesRota") {
      // Se está vendo detalhes, minimiza para navegar
      setSheetContent("navegandoRota");
    }
  };

  const handleCloseNavigation = () => {
    // Fecha a navegação e volta para a tela de detalhes da rota
    setSheetContent("rotas");
    setSheetHeight("70%");
    setCurrentStepIndex(0);
  };

  // Auto-centralizar quando mudar de passo
  useEffect(() => {
    if (
      (sheetContent === "detalhesRota" || sheetContent === "navegandoRota") &&
      routeDetails?.segments?.[currentStepIndex]
    ) {
      centerOnStep(currentStepIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex]);

  // Limpa rotas quando mudar de contexto (exceto quando está navegando)
  useEffect(() => {
    // Se não está em contexto de rotas/detalhes/navegação, limpa as rotas
    if (
      sheetContent !== "rotas" &&
      sheetContent !== "detalhesRota" &&
      sheetContent !== "navegandoRota"
    ) {
      setRoutePath(null);
      setRouteDetails(null);
    }
  }, [sheetContent]);

  const renderSheetContent = () => {
    if (sheetContent === "linhas") {
      return <LinhasOnibus onLinhaSelect={handleLinhaSelect} />;
    }
    if (sheetContent === "horarios") {
      return (
        <PartidasLinha
          routeId={selectedRouteId}
          routeShortName={selectedRouteShortName}
          onBack={handleBackToLinhas}
          onVehiclesUpdate={handleVehiclesUpdate}
          onVehicleClick={handleVehicleClick}
        />
      );
    }
    if (sheetContent === "detalhesRota") {
      return (
        <DetalhesRotaPlanner
          routeDetails={routeDetails}
          onBack={handleBackToRoutePlanner}
          onStepClick={handleStepClick}
        />
      );
    }
    if (sheetContent === "rotas") {
      return (
        <RoutePlanner
          onRouteFound={handleRouteFound}
          onViewDetails={handleViewRouteDetails}
          onVehiclesUpdate={handleVehiclesUpdate}
          onVehicleClick={handleVehicleClick}
        />
      );
    }
    if (sheetContent === "ponto") {
      return (
        <PartidasParada stopId={selectedStopId} onClose={handleCloseSheet} />
      );
    }
    if (sheetContent === "info") {
      return (
        <div style={{ padding: "20px" }}>
          <h2>Sistema de Transporte</h2>
          <p>Itapecerica da Serra</p>
          <p style={{ marginTop: "20px", color: "#666" }}>
            Use o mapa para visualizar pontos de ônibus e rotas.
          </p>
          <p style={{ color: "#666" }}>
            Clique em "Linhas" para ver todas as linhas disponíveis.
          </p>
          <p style={{ color: "#666" }}>Use "Rotas" para planejar sua viagem.</p>
        </div>
      );
    }
    return null;
  };

  const getCurrentStepInfo = () => {
    if (!routeDetails?.segments?.[currentStepIndex]) return null;
    const segment = routeDetails.segments[currentStepIndex];
    const isWalk = segment.mode === "WALK";

    return {
      icon: isWalk ? <FaWalking /> : <FaBus />,
      text: isWalk
        ? `Caminhe ${Math.ceil((segment.distance || 0) / 100) * 100}m`
        : `${segment.routeShortName || "Ônibus"} - ${
            segment.routeLongName || "Linha de ônibus"
          }`,
    };
  };

  return (
    <div className="App">
      <MapGoogle
        onMarkerClick={handleMarkerClick}
        routePath={routePath}
        centerMapPosition={centerMapPosition}
        vehicles={vehicles}
        focusVehicle={focusVehicle}
        userLocation={userLocation}
        nearbyStops={nearbyStops}
        tripOverlay={tripOverlay}
        tripOverlayLoading={tripOverlayLoading}
        tripOverlayError={tripOverlayError}
        onVehicleClick={handleVehicleMarkerClick}
        // Passa filtros para exibir apenas a linha/veículo desejado
        visibleRouteShortNames={trackingRouteShortNames}
        visibleVehicleId={focusedVehicleId}
      />

      {/* Navegador de passos - aparece quando está navegando ou vendo detalhes da rota */}
      {(sheetContent === "detalhesRota" || sheetContent === "navegandoRota") &&
        routeDetails?.segments && (
          <RouteStepNavigator
            currentStep={currentStepIndex}
            totalSteps={routeDetails.segments.length}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
            currentStepInfo={getCurrentStepInfo()}
            onToggleSheet={handleToggleSheet}
            onClose={
              sheetContent === "navegandoRota" ? handleCloseNavigation : null
            }
          />
        )}

      <BottomSheet
        isOpen={sheetContent !== null && sheetContent !== "navegandoRota"}
        onClose={handleCloseSheet}
        height={sheetHeight}
      >
        {renderSheetContent()}
      </BottomSheet>

      <FloatingNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onNearbyStopsClick={handleNearbyStopsClick}
        nearbyStopsLoading={nearbyStopsLoading}
        nearbyStopsActive={
          nearbyStops.length > 0 && !nearbyStopsLoading && !nearbyStopsError
        }
      />
    </div>
  );
}

export default PlannerPage;
